import React from 'react';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';
import FormContainer from '@components/layout/form-container';

import ButtonLoadingFixedCtrl from '@webapp/controllers/button-loading-fixed';
import UpdateStreamingPlatformsCtrl from '@webapp/controllers/form/update-streaming-platforms';

import AppState from '@webapp/store/state';

import SimpleArtist from '@common/types/artist/simple-artist';
import { validateAllPlatforms } from '@webapp/utils/validators/artist';
import { UPDATE_ARTISTS_PLATFORMS } from '@webapp/store/artist/actions';
import { UpdateArtistsPlatforms } from '@webapp/store/artist/action-creators';
import { Dispatch } from 'redux';
import { ValidateReleaseArtists } from '../../../store/release/action-creators';

interface RepairReleaseCtrlProps {
  className?: string;
  title: React.ReactNode;
  releaseId?: string;
  ownerId?: string;
  artists: SimpleArtist[];
  activeArtist?: SimpleArtist;
  isLoading: boolean;
  updateArtistsPlatforms: (
    ownerId: string,
    releaseId: string,
    artists: SimpleArtist[]
  ) => UpdateArtistsPlatforms;
  validateReleaseArtists: (
    artistId: string,
    releaseId: string
  ) => ValidateReleaseArtists;
}

const mapStateToProps = (state: AppState) => ({
  releaseId: state.release.release?.id,
  ownerId: state.artist.active?.id,
  artists: state.release.allReleaseArtists,
  activeArtist: state.artist.active,
  isLoading: !!state.ui.loading[UPDATE_ARTISTS_PLATFORMS],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateArtistsPlatforms: (
    ownerId: string,
    releaseId: string,
    artists: SimpleArtist[]
  ) => dispatch(new UpdateArtistsPlatforms(artists, ownerId, releaseId)),
  validateReleaseArtists: (artistId: string, releaseId: string) =>
    dispatch(new ValidateReleaseArtists(artistId, releaseId)),
});

export const RepairReleaseCtrl: React.FC<RepairReleaseCtrlProps> = ({
  className,
  title,
  artists,
  activeArtist,
  isLoading,
  ownerId,
  releaseId,
  updateArtistsPlatforms,
  validateReleaseArtists,
}) => {
  const [showErrors, setShowErrors] = React.useState(false);

  const [updates, setUpdates] = React.useState<SimpleArtist[]>([]);

  React.useEffect(() => {
    if (ownerId && releaseId) {
      validateReleaseArtists(ownerId, releaseId);
    }
  }, [validateReleaseArtists, releaseId, ownerId]);

  React.useEffect(() => {
    setUpdates(
      artists.map((artist) => ({
        ...artist,
        platforms: {
          ...artist.platforms,
          spotify:
            artist.platforms?.spotify ||
            (ownerId !== artist.id ? 'NEW' : undefined), // default NEW for all the artists other than the owner
        },
      }))
    );
  }, [artists, ownerId]);

  if (!releaseId || !ownerId || !artists.length) {
    return null;
  }

  const handleUpdate = (
    artist: SimpleArtist,
    platforms: SimpleArtist['platforms']
  ) => {
    setUpdates(
      updates.map((a) => {
        if (a.id === artist.id) {
          return {
            ...artist,
            platforms,
          };
        }
        return a;
      })
    );
  };

  const isValid =
    updates.length === artists.length &&
    updates.every((u) => validateAllPlatforms(u.platforms));

  const handleClick = () => {
    setShowErrors(true);
    if (isValid) {
      updateArtistsPlatforms(ownerId, releaseId, updates);
    }
  };

  return (
    <StyledFormContainer className={className}>
      <div className="mb-30">
        <Typography className="mb-10" type="header" small>
          {title}
        </Typography>
        <Typography opacity=".7">
          <Translate id="RELEASE.STREAMING.TEXT" />
        </Typography>
      </div>

      {updates.map((artist) => (
        <Box key={artist.id} className="mb-15">
          <BoxHeader
            title={artist.name}
            // text={<Translate id="RELEASE.REPAIR.BOX.TEXT" />}
          />
          <UpdateStreamingPlatformsCtrl
            className="mt-10"
            dontHide
            lockChangeProfile={
              !(artist.id === activeArtist?.id || !artist.ownerId)
            }
            artistName={artist.name}
            platforms={artist.platforms}
            externalIds={artist.externalIds}
            showErrors={showErrors}
            dataType="artist"
            onChange={(platforms) => handleUpdate(artist, platforms)}
          />
        </Box>
      ))}

      <ButtonLoadingFixedCtrl
        disabled={!isValid}
        onClick={handleClick}
        isLoading={isLoading}
      >
        <Translate id="RELEASE.REPAIR.TITLE" />
      </ButtonLoadingFixedCtrl>
    </StyledFormContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RepairReleaseCtrl);

const StyledFormContainer = styled(FormContainer)`
  margin: 0 auto;
  min-height: calc(100vh - ${({ theme }) => theme.sizes.navbarMobile});

  @media ${({ theme }) => theme.device.tablet} {
    margin: 0 auto;
    min-height: calc(100vh - ${({ theme }) => theme.sizes.navbar});
  }
`;
